import { useState } from 'react';
import Modal from '@mui/material/Modal';
import { MdVisibility as ViewIcon, MdEdit as EditIcon, MdAdminPanelSettings as AdminIcon, MdPerson as PersonIcon, MdGroups as TeamIcon } from 'react-icons/md';
import { IoClose as Close } from 'react-icons/io5';
import ToggleStyleSwitch from '@CommonRepresentationalDisplay/Switch/ToggleStyleSwitch';
import RemoveCollaboratorForm from '@CommonForms/RemoveCollaboratorForm';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import ErrorHandler from '@Root/ErrorHandler';
import './PermissionsView.scss';

export default function PermissionsView(props) {

    const [readPermission, setReadPermission] = useState(props.permissions.allowView);
    const [writePermission, setWritePermission] = useState(props.permissions.allowCreate);
    const [adminPermission, setAdminPermission] = useState(props.permissions.allowAdmin);
    const [disabled, setDisabled] = useState(
        props.type === 'user' ? (props.user?.id === props.project?.ownerUserId || props.user?.id === props.team?.ownerUserId || props.user?.id === props.org?.ownerUserId) :
        false
    )
    const [showRemoveUserForm, setShowRemoveUserForm] = useState(false);
    const [childType, setChildType] = useState()
    const [loading, setLoading] = useState(false);
    const projectService = new ProjectService();
    const teamService = new TeamService();
    const orgService = new OrgService();
    const errorHandler = new ErrorHandler();
    const className = 'permissionsView';

    const handleUpdatePermissions = async(e) => {
        e.preventDefault();
        try {
            setLoading(true);
            let permissions = [];
            if(readPermission) {
                permissions.push('read');
                if(writePermission){
                    permissions.push('write');
                    if(adminPermission){
                        permissions.push('admin');
                    }
                }
            }
            if(props.parentType === 'org') {
                let body = {
                    userId: props.user?.id,
                    orgId: props.org?.id,
                    childType: 'user',
                    permissions: JSON.stringify(permissions)
                }
                await orgService.updateContributorPermissionInOrg(body);
            }
            if(props.parentType === 'project') {
                if(props.type === 'user') {
                    let body = {
                        userId: props.user?.id,
                        projectId: props.project?.id,
                        childType: 'user',
                        permissions: JSON.stringify(permissions)
                    }
                    await projectService.updateContributorPermissionInProject(body);
                }
                if(props.type === 'team') {
                    let body = {
                        teamId: props.team?.id,
                        projectId: props.project?.id,
                        childType: 'team',
                        permissions: JSON.stringify(permissions)
                    }
                    await projectService.updateContributorPermissionInProject(body);
                }
            }
            if(props.parentType === 'team') {
                let body = {
                    userId: props.user?.id,
                    teamId: props.team?.id,
                    childType: 'user',
                    permissions: JSON.stringify(permissions)
                }
                await teamService.updateContributorPermissionInTeam(body);
            }
            
            // Show success snackbar
            SnackbarManager.show(
                'Permissions updated successfully!',
                'success'
            );
            
            props.reloadData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
            // Show error snackbar
            SnackbarManager.show(
                'Failed to update permissions. Please try again.',
                'error'
            );
        } finally {
            setLoading(false);
        }
    }

    const closeAllPopup = () => {
        setShowRemoveUserForm(false);
        props.closePopup()
    }

    const handleReadPermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked){
            setReadPermission(true);
        } else {
            setReadPermission(false);
            setWritePermission(false);
            setAdminPermission(false);
        }
    }

    const handleWritePermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked && readPermission){
            setWritePermission(true);
        } else {
            setWritePermission(false);
            setAdminPermission(false);
        }
    }

    const handleAdminPermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked && readPermission && writePermission){
            setAdminPermission(true);
        } else {
            setAdminPermission(false);
        }
    }

    const handleRemoveUserForm = () => {
        setChildType('user')
        setShowRemoveUserForm(!showRemoveUserForm);
    }

    const handleRemoveTeamForm = () => {
        setChildType('team')
        setShowRemoveUserForm(!showRemoveUserForm);
    }

    const displayName = props.type === 'user' ? props.user?.fullName : props.team?.teamName;

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}>
            <div className={className}>
                <div className={`${className}__container`}>
                    {/* Header */}
                    <div className={`${className}__header`}>
                        <div className={`${className}__header__left`}>
                            <div className={`${className}__header__avatar`}>
                                {props.type === 'user' ? <PersonIcon /> : <TeamIcon />}
                            </div>
                            <div className={`${className}__header__info`}>
                                <h2 className={`${className}__header__title`}>{displayName}</h2>
                                {disabled && (
                                    <span className={`${className}__header__badge`}>Owner</span>
                                )}
                            </div>
                        </div>
                        <button 
                            className={`${className}__header__close`} 
                            onClick={props.closePopup}
                            disabled={loading}>
                            <Close />
                        </button>
                    </div>

                    {/* Permissions Section */}
                    <div className={`${className}__permissions`}>
                        <h3 className={`${className}__permissions__title`}>Permissions</h3>
                        
                        {/* View Permission */}
                        <div className={`${className}__permission`}>
                            <div className={`${className}__permission__icon view`}>
                                <ViewIcon />
                            </div>
                            <div className={`${className}__permission__content`}>
                                <div className={`${className}__permission__header`}>
                                    <div className={`${className}__permission__info`}>
                                        <h4 className={`${className}__permission__name`}>View All</h4>
                                        <p className={`${className}__permission__desc`}>
                                            {displayName} will be able to view all items
                                        </p>
                                    </div>
                                    <ToggleStyleSwitch
                                        onChange={handleReadPermissionsCheck}
                                        checked={readPermission}
                                        disabled={disabled}/>
                                </div>
                            </div>
                        </div>

                        {/* Create/Edit Permission */}
                        <div className={`${className}__permission ${!readPermission ? 'disabled' : ''}`}>
                            <div className={`${className}__permission__icon edit`}>
                                <EditIcon />
                            </div>
                            <div className={`${className}__permission__content`}>
                                <div className={`${className}__permission__header`}>
                                    <div className={`${className}__permission__info`}>
                                        <h4 className={`${className}__permission__name`}>Create And Edit</h4>
                                        <p className={`${className}__permission__desc`}>
                                            {displayName} will be able to create and edit items
                                        </p>
                                    </div>
                                    <ToggleStyleSwitch
                                        onChange={handleWritePermissionsCheck}
                                        checked={writePermission && readPermission}
                                        disabled={!readPermission || disabled}/>
                                </div>
                            </div>
                        </div>

                        {/* Admin Permission */}
                        <div className={`${className}__permission ${(!readPermission || !writePermission) ? 'disabled' : ''}`}>
                            <div className={`${className}__permission__icon admin`}>
                                <AdminIcon />
                            </div>
                            <div className={`${className}__permission__content`}>
                                <div className={`${className}__permission__header`}>
                                    <div className={`${className}__permission__info`}>
                                        <h4 className={`${className}__permission__name`}>Admin Privileges</h4>
                                        <p className={`${className}__permission__desc`}>
                                            {displayName} will be able to add/remove users and edit permissions
                                        </p>
                                    </div>
                                    <ToggleStyleSwitch
                                        onChange={handleAdminPermissionsCheck}
                                        checked={adminPermission && writePermission && readPermission}
                                        disabled={!writePermission || disabled}/>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={`${className}__actions`}>
                        {props.activeUserPermissions.allowAdmin && !disabled && (
                            <button
                                className={`${className}__actions__remove`}
                                onClick={props.type === 'user' ? handleRemoveUserForm : handleRemoveTeamForm}
                                disabled={loading}>
                                Remove {props.type === 'user' ? 'User' : 'Team'}
                            </button>
                        )}
                        <div className={`${className}__actions__buttons`}>
                            <button
                                className={`${className}__actions__cancel`}
                                onClick={props.closePopup}
                                disabled={loading}>
                                Cancel
                            </button>
                            <button
                                className={`${className}__actions__update`}
                                onClick={handleUpdatePermissions}
                                disabled={disabled || loading}>
                                {loading ? 'Updating...' : 'Update Permissions'}
                            </button>
                        </div>
                    </div>
                </div>

                {showRemoveUserForm && (
                    <RemoveCollaboratorForm
                        open={showRemoveUserForm}
                        closePopup={handleRemoveUserForm}
                        user={props.user}
                        closeView={props.closePopup}
                        org={props.org}
                        team={props.team}
                        project={props.project}
                        childType={childType}
                        parentType={props.parentType}
                        reloadData={props.reloadData}
                        closeAllPopup={closeAllPopup}/>
                )}
            </div>
        </Modal>
    );
}
